import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { RecommededRoutePlanner2Response, RecommendedRouteNewTokenPayload } from '../interfaces';
import { catchError, finalize, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ToolsService } from 'src/app/services';

@Injectable({
  providedIn: 'root',
})
export class RecommededRouteListPlanner2 {
  // Service injections
  private _http = inject(HttpClient);
  private _toolsService = inject(ToolsService);
  
  // Service signals
  private _isTechMode = this._toolsService.isTechMode;
  
  // Signal states
  private _recommededRouteListPlanner2 = signal<RecommededRoutePlanner2Response[] | undefined>(undefined);
  public recommededRouteListPlanner2 = this._recommededRouteListPlanner2.asReadonly();
  
  private _isLoadingRecommededRouteListPlanner2 = signal<boolean>(false);
  public isLoadingRecommededRouteListPlanner2 = this._isLoadingRecommededRouteListPlanner2.asReadonly();
  
  // Setters
  public setRecommededRouteListPlanner2(response: RecommededRoutePlanner2Response[]): void {
    this._recommededRouteListPlanner2.set(response);
  }

  public removeRecommededRoute(token: string): void {
    this._recommededRouteListPlanner2.update(routes =>
      routes?.filter(route => route.token !== token) ?? []
    );
  }
  
  // HTTP Requests
  public getRecommededRouteListPlanner2ServiceSignal(clientId: number, clientType: string, appId: number): void {
    this._getRecommededRouteListPlanner2(clientId, clientType, appId).subscribe({
      next: (response: RecommededRoutePlanner2Response[]) => {
        
        if (!this._isTechMode()){
          // Public listing: only published routes that are not 'Solo QR' (private).
          this._recommededRouteListPlanner2.set(response.filter(route => route.is_active === true && route.private !== true));
        } else {
          this._recommededRouteListPlanner2.set(response);
        }
      },
      error: (err) => {
        this._recommededRouteListPlanner2.set([]);
        console.error('Error getting recommended route list planner 2', err);
      }
    });
  }
  
  private _getRecommededRouteListPlanner2(clientId: number, clientType: string, appId: number): Observable<RecommededRoutePlanner2Response[]> {
    this._isLoadingRecommededRouteListPlanner2.set(true);

    const url = `${environment.apiAssistantUrlBase}/api/recommended-route-list/${clientId}/${clientType}/${appId}`;
    return this._http.get<RecommededRoutePlanner2Response[]>(url).pipe(
      catchError((err) => {
        return throwError(() => new Error('Error getting recommended route list planner 2', { cause: err }));
      }),
      map((response: RecommededRoutePlanner2Response[]) => {
        return response;
      }),
      finalize(() => {
        this._isLoadingRecommededRouteListPlanner2.set(false);
      })
    );
  }
  
  public getRecommededRoutePlanner2NewToken(payload: RecommendedRouteNewTokenPayload): Observable<{ token: string }> {
    const url = `${environment.apiAssistantUrlBase}/api/recommended-route/instantiate`;
    return this._http.post<RecommendedRouteNewTokenPayload>(url, payload).pipe(
      catchError((err) => {
        return throwError(() => new Error('Error getting recommended route list', { cause: err }));
      }),
      map((response: { token: string }) => {
        return response;
      })
    );
  }
}
